import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, searchAPI } from '../services/api';
import { getCurrencyConfig, formatMoney } from '../utils/currency';
import './SearchBar.css';

// Función helper para extraer texto plano del HTML
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

function SearchBar({ searchPlaceholder, sesionesLabel }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const currency = getCurrencyConfig(user);
  const money = (value) => formatMoney(value, { ...currency, decimals: 2 });

  useEffect(() => {
    authAPI.check().then((response) => {
      if (response.data?.authenticated && response.data?.user) {
        setUser(response.data.user);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (query.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        performSearch(query);
      }, 300); // Debounce de 300ms

      return () => clearTimeout(timeoutId);
    } else {
      setResults(null);
      setShowResults(false);
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    try {
      setLoading(true);
      const response = await searchAPI.search(searchQuery);
      setResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Error en búsqueda:', error);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (type, id) => {
    setShowResults(false);
    setQuery('');
    
    switch (type) {
      case 'cliente':
        navigate('/clientes', { state: { openClienteId: id } });
        break;
      case 'cita':
        navigate('/agenda', { state: { targetCitaId: id } });
        break;
      case 'consulta':
        navigate('/sesiones', { state: { targetConsultaId: id } });
        break;
      case 'factura':
        navigate('/facturacion', { state: { targetFacturaId: id } });
        break;
      default:
        break;
    }
  };

  const totalResults = results
    ? (results.clientes?.length || 0) +
      (results.facturas?.length || 0) +
      (results.citas?.length || 0) +
      (results.consultas?.length || 0)
    : 0;

  return (
    <div className="search-bar-container" ref={searchRef}>
      <div className="search-input-wrapper">
        <input
          type="text"
          className="search-input"
          placeholder={searchPlaceholder || "Buscar clientes, citas, sesiones..."}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results && totalResults > 0) {
              setShowResults(true);
            }
          }}
        />
        {loading && <span className="search-loading">⏳</span>}
      </div>

      {showResults && results && (
        <div className="search-results" ref={resultsRef}>
          {totalResults === 0 ? (
            <div className="search-result-empty">
              No se encontraron resultados para "{query}"
            </div>
          ) : (
            <>
              {results.clientes && results.clientes.length > 0 && (
                <div className="search-result-section">
                  <div className="search-result-section-title">
                    👤 Clientes ({results.clientes.length})
                  </div>
                  {results.clientes.map((cliente) => (
                    <div
                      key={cliente.id}
                      className="search-result-item"
                      onClick={() => handleResultClick('cliente', cliente.id)}
                    >
                      <div className="search-result-item-title">
                        {cliente.nombre} {cliente.apellidos}
                      </div>
                      <div className="search-result-item-meta">
                        {cliente.email && `${cliente.email} • `}
                        {cliente.telefono && `${cliente.telefono} • `}
                        {cliente.ciudad && cliente.ciudad}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {results.citas && results.citas.length > 0 && (
                <div className="search-result-section">
                  <div className="search-result-section-title">
                    📅 Citas ({results.citas.length})
                  </div>
                  {results.citas.map((cita) => (
                    <div
                      key={cita.id}
                      className="search-result-item"
                      onClick={() => handleResultClick('cita', cita.id)}
                    >
                      <div className="search-result-item-title">{cita.titulo}</div>
                      <div className="search-result-item-meta">
                        {new Date(cita.fecha_hora).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {' • '}
                        {cita.estado}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {results.facturas && results.facturas.length > 0 && (
                <div className="search-result-section">
                  <div className="search-result-section-title">
                    💰 Facturas ({results.facturas.length})
                  </div>
                  {results.facturas.map((factura) => (
                    <div
                      key={factura.id}
                      className="search-result-item"
                      onClick={() => handleResultClick('factura', factura.id)}
                    >
                      <div className="search-result-item-title">
                        Factura {factura.numero || `#${factura.id}`}
                      </div>
                      <div className="search-result-item-meta">
                        {factura.cliente_nombre ? `${factura.cliente_nombre} ${factura.cliente_apellidos || ''} • ` : ''}
                        {factura.fecha_emision ? new Date(factura.fecha_emision).toLocaleDateString('es-ES') : ''}
                        {factura.total !== undefined && factura.total !== null ? ` • ${money(factura.total)}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {results.consultas && results.consultas.length > 0 && (
                <div className="search-result-section">
                  <div className="search-result-section-title">
                    📝 {sesionesLabel ? sesionesLabel.charAt(0).toUpperCase() + sesionesLabel.slice(1) : 'Sesiones'} ({results.consultas.length})
                  </div>
                  {results.consultas.map((consulta) => (
                    <div
                      key={consulta.id}
                      className="search-result-item"
                      onClick={() => handleResultClick('consulta', consulta.id)}
                    >
                      <div className="search-result-item-title">
                        {new Date(consulta.fecha_consulta).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                      <div className="search-result-item-meta">
                        {consulta.numero_sesion && `Sesión #${consulta.numero_sesion}`}
                        {consulta.numero_sesion && consulta.objetivos && ' - '}
                        {consulta.objetivos && (() => {
                          const textContent = stripHtml(consulta.objetivos);
                          return textContent.substring(0, 80) + (textContent.length > 80 ? '...' : '');
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;


